import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGame } from '../context/GameContext';
import { createSession } from '../services/api';
import { CreateSessionData } from '../types';

interface FormErrors {
  nombre_completo?: string;
  email?: string;
  general?: string;
}

const Registration: React.FC = () => {
  const navigate = useNavigate();
  const { setSessionId, setParticipantData } = useGame();
  const { t } = useTranslation('registration');

  const [formData, setFormData] = useState<CreateSessionData>({
    nombre_completo: '',
    email: '',
    accepted_terms: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.nombre_completo.trim()) {
      newErrors.nombre_completo = t('validation.nameRequired');
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!formData.accepted_terms) {
      setErrors({ general: t('validation.termsRequired') });
      return;
    }

    setLoading(true);
    try {
      const result = await createSession(formData);
      setParticipantData(formData);
      setSessionId(result.session_id);
      navigate('/trivia');
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string } | undefined;
      if (err?.code === 'EMAIL_DUPLICATE') {
        setErrors({ email: t('common:errors.EMAIL_DUPLICATE') });
      } else {
        setErrors({ general: err?.code ? t(`common:errors.${err.code}`) : t('errors.connectionError') });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (error?: string) =>
    `w-full bg-white/5 border rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 transition-all ${
      error
        ? 'border-red-400 focus:ring-red-400/50'
        : 'border-white/20 focus:ring-blue-400/50 focus:border-blue-400'
    }`;

  return (
    <div
      className="min-h-screen flex flex-col px-6 py-10"
      style={{ background: 'linear-gradient(135deg, #00082f 0%, #001055 60%, #00082f 100%)' }}
    >
      <div className="w-full max-w-sm mx-auto flex flex-col gap-6">
        {/* Header */}
        <div className="text-center">
          <img src="/assets/logo.png" alt="Zepo" className="h-8 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('header.title')}
          </h1>
          <p className="text-blue-200 text-base">
            {t('header.subtitle')}{' '}
            <span className="text-white/40 text-sm">{t('header.disclaimer')}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-blue-300 mb-1">
              {t('fields.nameLabel')}
            </label>
            <input
              type="text"
              className={inputClass(errors.nombre_completo)}
              placeholder={t('fields.namePlaceholder')}
              value={formData.nombre_completo}
              onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
            />
            {errors.nombre_completo && (
              <p className="text-red-400 text-sm mt-1">{errors.nombre_completo}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-300 mb-1">
              {t('fields.emailLabel')}
            </label>
            <input
              type="email"
              className={inputClass(errors.email)}
              placeholder={t('fields.emailPlaceholder')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div className="flex items-start gap-3 mt-2">
            <input
              type="checkbox"
              id="terms"
              className="mt-1 w-5 h-5 rounded border-white/20 bg-white/5 cursor-pointer flex-shrink-0"
              checked={formData.accepted_terms}
              onChange={(e) => setFormData({ ...formData, accepted_terms: e.target.checked })}
            />
            <label htmlFor="terms" className="text-sm text-white/70 cursor-pointer">
              {t('fields.termsLabel')}{' '}
              <a
                href="/terminos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 underline hover:text-blue-300"
              >
                {t('fields.termsLink')}
              </a>{' '}
              {t('fields.termsSuffix')}
            </label>
          </div>

          {errors.general && (
            <div className="glass-card border-red-400/30 p-3">
              <p className="text-red-300 text-sm text-center">{errors.general}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white font-bold text-lg rounded-2xl px-8 min-h-[56px] py-4 transition-all active:scale-[0.98] mt-2 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('submit.loading')}
              </>
            ) : (
              t('submit.default')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Registration;
